import { Component, computed, effect, inject, input, OnInit, output, ResourceRef, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';

// Third-Party
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { NgbModal, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';

// Services
import { ToastService } from '../../../shared/services/toast.service';
import { SearchConfigService } from '../../services/search-config.service';
import { ContextService } from '../../services/context.service';

// Components
import { LoadingButtonComponent } from "../../../shared/components/loading-button/loading-button.component";
import { ConfirmModal } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { SearchPreviewComponent } from '../../../search-preview/components/search-preview/search-preview.component';
import { FieldLoaderComponent } from '../../../shared/components/field-loader/field-loader.component';

// RxJS
import { finalize, forkJoin, of, switchMap } from 'rxjs';

// Models
import { ContextSetupSet } from '../../models/context-setup.model';
import { ContextDetail } from '../../models/context.model';

export interface Models {
  model: string
  description: string
  discontinuationDate: any
  createTime: string
}

type ContextSetupForm = ContextSetupSet & {
  allowedDomains: string;
};

@Component({
  selector: 'app-context-setup',
  imports: [ReactiveFormsModule, LoadingButtonComponent, NgxPermissionsModule, FieldLoaderComponent],
  templateUrl: './context-setup.component.html',
  styleUrl: './context-setup.component.scss'
})
export class ContextSetupComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastSrv = inject(ToastService);
  private readonly searchConfigSrv = inject(SearchConfigService);
  private readonly contextSrv = inject(ContextService);
  private readonly modalSrv = inject(NgbModal);
  private readonly offcanvasService = inject(NgbOffcanvas);
  private readonly permissionsSrv = inject(NgxPermissionsService);

  readonly hasMadPermission = signal(false);

  contextId = input.required<string>();
  contextDetail = input.required<ResourceRef<ContextDetail | undefined>>();

  navList = [{ label: 'Lista Contesti', routePath: '/contexts' }, { label: 'Setup Contesto', routePath: '/contexts/setup' }];

  // Stati di azione (bottoni)
  readonly loading = signal(false);
  readonly previewLoading = signal(false);
  readonly deleteLoading = signal(false);

  // Risorse di lettura
  protected readonly setupRes = rxResource({
    params: () => this.contextId(),
    stream: ({ params }) => this.searchConfigSrv.getContextSetup(params),
  });

  protected readonly domainsRes = rxResource({
    params: () => (this.hasMadPermission() ? this.contextId() : undefined),
    stream: ({ params }) => this.contextSrv.getContextDomains(params),
  });

  protected readonly modelsRes = rxResource({ stream: () => this.searchConfigSrv.getModels() });
  protected readonly templatesRes = rxResource({ stream: () => this.searchConfigSrv.getTemplates() });
  protected readonly languagesRes = rxResource({ stream: () => this.searchConfigSrv.getLanguages() });

  // Flag per i field-loader
  protected readonly setupLoading = computed(() => this.setupRes.isLoading());
  protected readonly templatesLoading = computed(() => this.setupRes.isLoading() || this.templatesRes.isLoading());
  protected readonly modelsLoading = computed(() => this.setupRes.isLoading() || this.modelsRes.isLoading());
  protected readonly languagesLoading = computed(() => this.setupRes.isLoading() || this.languagesRes.isLoading());
  protected readonly domainsLoading = computed(() => this.domainsRes.isLoading());

  // Per disabilitare i bottoni finché non è tutto pronto
  protected readonly anyLoading = computed(() =>
    this.setupRes.isLoading() ||
    this.templatesRes.isLoading() ||
    this.modelsRes.isLoading() ||
    this.languagesRes.isLoading() ||
    this.domainsRes.isLoading()
  );

  setupContextForm: FormGroup<{
    [K in keyof ContextSetupForm]: ReturnType<typeof this.fb.control<ContextSetupForm[K]>>;
  }>;

  stateChanged = output<any>();

  private currentDomainList: string[] = [];

  constructor() {
    this.setupContextForm = this.fb.group({
      summarizationModel: this.fb.control('', { validators: [Validators.required] }),
      languageCode: this.fb.control(''),
      ignoreLowRelevant: this.fb.control(false),
      topResults: this.fb.control(5, { validators: [Validators.min(1)] }),
      mainGoal: this.fb.control(''),
      focusItems: this.fb.control(''),
      answerLength: this.fb.control(''),
      accuracy: this.fb.control(''),
      toneOfVoice: this.fb.control(''),
      additionalInstructions: this.fb.control(''),
      format: this.fb.control(''),
      integrationStatus: this.fb.control<ContextSetupSet['integrationStatus']>('BACKEND_INTEGRATION_ONLY'),
      allowedDomains: this.fb.control('')
    });

    this.setupContextForm.valueChanges.subscribe(() => {
      if (this.setupContextForm.dirty) {
        this.stateChanged.emit(true);
      }
    });

    // Precompilazione setup
    effect(() => {
      const setup = this.setupRes.value();
      if (setup) {
        this.setupContextForm.patchValue(setup);
      }
    });

    // Precompilazione domini (solo ROLE_MAD)
    effect(() => {
      const res = this.domainsRes.value();
      if (res) {
        this.currentDomainList = res.domains;
        this.setupContextForm.patchValue({ allowedDomains: res.domains.join(', ') });
      }
    });

    // Form disabilitata durante il caricamento del setup
    effect(() => {
      if (this.setupRes.isLoading()) {
        this.setupContextForm.disable({ emitEvent: false });
      } else {
        this.setupContextForm.enable({ emitEvent: false });
      }
    });

    // Log errori di lettura
    effect(() => {
      const err = this.domainsRes.error();
      if (err) console.error('Errore durante il recupero dei domini abilitati del contesto', err);
    });
  }

  ngOnInit(): void {
    this.permissionsSrv.hasPermission('ROLE_MAD').then(has => this.hasMadPermission.set(has));
  }

  integerValidator(control: FormControl) {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return Number.isInteger(value) ? null : { integer: true };
  }

  onSubmit() {
    if (this.setupContextForm.valid && this.contextDetail()) {
      const { allowedDomains, ...contextSetup } = this.setupContextForm.getRawValue();
      this.saveContextSetup(contextSetup, allowedDomains);
    } else {
      this.toastSrv.show({ type: 'error', message: 'Per favore, completa tutti i campi richiesti.' });
    }
  }

  deleteContext() {
    const modalRef = this.modalSrv.open(ConfirmModal, { centered: true, windowClass: 'qai-modal', size: 'lg' });
    modalRef.componentInstance.title = $localize`Elimina Contesto`;
    modalRef.componentInstance.body = $localize`Stai eliminado il contesto: "${this.contextDetail()?.value()?.name}", digita il nome del contesto per confermare`;
    modalRef.componentInstance.enableMotivationForm = true;
    modalRef.componentInstance.deleteItemKeyValue = this.contextDetail()?.value()?.name;
    modalRef.result.then(
      (result: { motivation: string }) => {
        console.debug(`Closed with: ${result}`);
        this.deleteLoading.set(true);
        this.contextSrv.deleteContext(this.contextId())
          .pipe(finalize(() => this.deleteLoading.set(false)))
          .subscribe({
            next: () => {
              this.toastSrv.show({ type: 'success', message: 'Contesto eliminato con successo' });
              this.router.navigate(['/contexts']);
            },
            error: (err) => {
              this.toastSrv.show({ type: 'error', message: 'Errore durante l\'eliminazione del contesto' });
              console.error(err);
            }
          });
      },
      () => {
        console.debug(`Dismissed`);
      },
    );
  }

  get topResults() {
    return this.setupContextForm.get('topResults');
  }

  openPreview() {
    if (this.setupContextForm.valid && this.contextDetail()) {
      const contextSetup = this.setupContextForm.getRawValue() as ContextSetupSet;
      this.saveContextSetupPreview(contextSetup);
    } else {
      this.toastSrv.show({ type: 'error', message: 'Per favore, completa tutti i campi richiesti.' });
    }
  }

  /** Salvataggio normale del contesto */
  private saveContextSetup(contextSetup: ContextSetupSet, allowedDomains?: string) {
    if (!this.contextDetail() || !contextSetup) return;

    this.loading.set(true);

    if (contextSetup.languageCode === 'null') {
      delete contextSetup.languageCode;
    }

    this.searchConfigSrv.updateContextSetup(this.contextId(), contextSetup).pipe(
      switchMap(() => {
        if (allowedDomains !== undefined && this.hasMadPermission()) {
          return this.syncDomains$(allowedDomains);
        }
        return of(null);
      }),
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: () => {
        this.setupContextForm.markAsPristine();
        this.stateChanged.emit(false);

        this.toastSrv.show({
          type: 'success',
          message: 'Configurazione contesto aggiornata con successo!'
        });

        if (allowedDomains !== undefined) {
          const parsed = this.parseDomains(allowedDomains);
          const normalize = (arr: string[]) => [...arr].sort().join('|');
          if (normalize(parsed) !== normalize(this.currentDomainList)) {
            this.currentDomainList = parsed;
            this.toastSrv.show({
              type: 'info',
              autoHide: false,
              message: 'La sincronizzazione dei domini è stata salvata ma potrebbe richiedere circa 15 minuti per essere propagata e diventare effettiva.'
            });
          }
        }
      },
      error: (err) => {
        console.error(err);
        this.toastSrv.show({
          type: 'error',
          message: 'Errore durante il salvataggio del contesto'
        });
      }
    });
  }

  private syncDomains$(domainsString: string) {
    const newDomains = this.parseDomains(domainsString);

    const toAdd = newDomains.filter(d => !this.currentDomainList.includes(d));
    const toRemove = this.currentDomainList.filter(d => !newDomains.includes(d));

    const calls = [];

    if (toAdd.length) {
      calls.push(this.contextSrv.addContextDomain(this.contextId(), toAdd));
    }

    if (toRemove.length) {
      calls.push(
        ...toRemove.map(domain => this.contextSrv.deleteContextDomain(this.contextId(), domain))
      );
    }

    if (!calls.length) {
      return of(null);
    }

    return forkJoin(calls);
  }

  /** Salvataggio del contesto in modalità preview */
  private saveContextSetupPreview(contextSetup: ContextSetupSet) {
    if (this.contextDetail() && contextSetup) {
      this.previewLoading.set(true);

      if (contextSetup.languageCode === 'null') {
        delete contextSetup.languageCode;
      }

      this.searchConfigSrv.updateContextSetupPreview(this.contextId(), contextSetup)
        .pipe(finalize(() => this.previewLoading.set(false)))
        .subscribe({
          next: () => {
            const offcanvasRef = this.offcanvasService.open(SearchPreviewComponent, {
              position: 'end',
              panelClass: 'w-50',
              backdrop: 'static',
              scroll: true
            });
            offcanvasRef.componentInstance.contextId = this.contextId();
            this.toastSrv.show({ type: 'info', message: 'Stai visualizzando un\'anteprima. Ricorda di salvare le modifiche.' });
          }
        });
    }
  }

  private parseDomains(domainsString: string): string[] {
    return domainsString
      .split(',')
      .map(d => d.trim())
      .filter(Boolean);
  }
}